/**
 * The factory — its far wall and its machines (parallax layers .3 and .6).
 * Gears turn with the belt: the motion code sets --spin on the section and
 * `.gear` reads it (sections/_factory.scss).
 *
 * @module scenes/factory
 */

import { circle, rect, scene } from './svg.js';

/**
 * A toothed gear.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} radius
 * @param {number} teeth
 * @param {boolean} reverse - Turns the other way (meshing pairs).
 * @returns {string}
 */
function gear(cx, cy, radius, teeth, reverse) {
    const points = [];
    const count = teeth * 4;

    for (let index = 0; index < count; index++) {
        const angle = (index / count) * Math.PI * 2;
        const r = index % 4 < 2 ? radius : radius * 0.8;
        points.push(`${(cx + Math.cos(angle) * r).toFixed(1)},${(cy + Math.sin(angle) * r).toFixed(1)}`);
    }

    return (
        `<g class="gear${reverse ? ' gear--reverse' : ''}">` +
        `<polygon points="${points.join(' ')}" class="fill-night-3"/>` +
        circle(cx, cy, (radius * 0.34).toFixed(1), 'fill-night-2') +
        circle(cx, cy, (radius * 0.1).toFixed(1), 'fill-dim') +
        '</g>'
    );
}

/** @returns {import('../lib/html.js').Markup} */
export function factoryWall() {
    let out = '';

    // Tall industrial windows, a few panes lit.
    for (let x = 60; x < 3600; x += 420) {
        out += rect(x, 30, 240, 340, 'fill-night-2', 'rx="4"');

        for (let row = 0; row < 5; row++) {
            for (let column = 0; column < 4; column++) {
                const lit = (column * 3 + row * 5 + x / 60) % 11 === 0;
                out += rect(
                    x + 12 + column * 57,
                    42 + row * 65,
                    45,
                    53,
                    lit ? 'fill-amber' : 'fill-night',
                    lit ? 'opacity=".35"' : '',
                );
            }
        }
    }

    // Pipes along the wall, a red valve on each drop.
    out +=
        '<path d="M0 418 H3600" class="stroke-amber" stroke-width="4" opacity=".3"/>' +
        '<path d="M0 440 H3600" class="stroke-night-3" stroke-width="12"/>';

    for (let x = 330; x < 3600; x += 640) {
        out += `<path d="M${x} 440 V600" class="stroke-night-3" stroke-width="12"/>${circle(x, 505, 15, 'fill-red')}`;
    }

    // Gears, in meshing pairs.
    [
        [180, 70],
        [1260, 90],
        [2280, 70],
        [3240, 85],
    ].forEach(([x, radius]) => {
        out +=
            gear(x, 600 - radius * 0.6, radius, 12, false) +
            gear(x + radius * 1.55, 600 - radius * 1.25, radius * 0.62, 8, true);
    });

    return scene('0 0 3600 600', out, 'xMinYMax meet');
}

/**
 * The machines between the wall and the line: tanks, hoppers and robot
 * arms, in silhouette.
 *
 * @returns {import('../lib/html.js').Markup}
 */
export function factoryMachines() {
    let out = '';

    for (let x = 200; x < 6000; x += 760) {
        const kind = Math.round(x / 760) % 3;

        if (kind === 0) {
            // A tank with bands and a gauge.
            out +=
                rect(x, 150, 170, 270, 'fill-night-3', 'rx="24"') +
                rect(x, 200, 170, 10, 'fill-night-2') +
                rect(x, 330, 170, 10, 'fill-night-2') +
                circle(x + 85, 265, 20, 'fill-night-2') +
                circle(x + 85, 265, 6, 'fill-amber') +
                rect(x + 70, 60, 30, 92, 'fill-night-3');
        } else if (kind === 1) {
            // A hopper on its legs.
            out +=
                `<polygon points="${x},110 ${x + 220},110 ${x + 140},260 ${x + 80},260" class="fill-night-3"/>` +
                rect(x + 90, 260, 40, 60, 'fill-night-3') +
                rect(x + 20, 120, 12, 300, 'fill-night-3') +
                rect(x + 188, 120, 12, 300, 'fill-night-3');
        } else {
            // A robot arm, bent over the line.
            out +=
                rect(x, 360, 120, 60, 'fill-night-3', 'rx="8"') +
                `<path d="M${x + 60} 360 L${x + 40} 200 L${x + 200} 130 L${x + 260} 250" ` +
                'class="stroke-night-3" stroke-width="28" stroke-linecap="round" stroke-linejoin="round"/>' +
                circle(x + 40, 200, 22, 'fill-night-2') +
                circle(x + 200, 130, 22, 'fill-night-2') +
                circle(x + 260, 250, 9, 'fill-red');
        }
    }

    return scene('0 0 6000 420', `<g opacity=".7">${out}</g>`, 'xMinYMax meet');
}
